import { useAuthContext } from '../contexts/AuthContext';
import { useEstabsContext } from '../contexts/EstabsContext';
import {
  SalonFlowFeatureKey,
  SalonFlowPlanKey,
  getRequiredUpgradeForFeature,
  normalizePlanKey,
  salonflowPlanHasFeature,
  getTrialRemainingDays,
  TrialRemaining,
} from './salonflowPlans';

/** Usuario apenas cliente (sem papel admin/worker/root no app interno). */
function isStandaloneClientUsage(
  user: {
    root?: boolean;
    admin?: boolean;
    worker?: boolean;
  } | null,
): boolean {
  return !!user && !user.root && !user.admin && !user.worker;
}

export type PlanAccess = {
  resolvedPlanKey: SalonFlowPlanKey;
  billingStatus?: string | null;
  billingExempt: boolean;
  /** Conta deve aplicar restricao por plano (papeis internos, incluindo root; cliente puro nao e bloqueado por feature administrativa). */
  restrictionsApply: boolean;
  /** Item no menu: workers somem se bloqueado; admin/root ve entrada com cadeado. */
  menuShowEntry: (planFeature?: SalonFlowFeatureKey) => boolean;
  hasFeature: (feature: SalonFlowFeatureKey) => boolean;
  isLocked: (feature: SalonFlowFeatureKey) => boolean;
  requiredUpgrade: (feature: SalonFlowFeatureKey) => SalonFlowPlanKey | null;
  trialRemaining: TrialRemaining | null;
};

export function usePlanAccess(): PlanAccess {
  const { user } = useAuthContext();
  const { estabSession } = useEstabsContext();

  const session = estabSession as {
    systemPlanKey?: string;
    billingStatus?: string;
    billingExempt?: boolean;
    trialEndsAt?: string | Date;
  };

  const billingExempt = session?.billingExempt === true;
  const resolvedPlanKey = billingExempt ? 'premium' : normalizePlanKey(session?.systemPlanKey);
  const billingStatus = session?.billingStatus ?? null;
  const trialRemaining = getTrialRemainingDays(session?.trialEndsAt);

  const bypassPlan = billingExempt || isStandaloneClientUsage(user ?? null);

  const restrictionsApply = !!user && !!user.uid && !bypassPlan;

  const menuShowEntry = (planFeature?: SalonFlowFeatureKey): boolean => {
    if (!planFeature || !restrictionsApply) return true;
    if (hasFeatureInner(planFeature)) return true;
    if (user?.admin || user?.root) return true;
    return false;
  };

  const hasFeatureInner = (feature: SalonFlowFeatureKey): boolean =>
    billingExempt || salonflowPlanHasFeature(resolvedPlanKey, feature);

  const hasFeature = (feature: SalonFlowFeatureKey): boolean => {
    if (!user || bypassPlan) return true;
    return hasFeatureInner(feature);
  };

  const requiredUpgrade = (feature: SalonFlowFeatureKey): SalonFlowPlanKey | null =>
    billingExempt || bypassPlan ? null : getRequiredUpgradeForFeature(feature);

  const isLocked = (feature: SalonFlowFeatureKey): boolean =>
    !hasFeature(feature);

  return {
    resolvedPlanKey,
    billingStatus,
    billingExempt,
    restrictionsApply,
    menuShowEntry,
    hasFeature,
    isLocked,
    requiredUpgrade,
    trialRemaining,
  };
}
